import { hasBrowserNotificationOptOut } from '@/lib/notifications/web-push-client';

import type {
  YourImaiFeedRow,
  YourImaiThreadCard,
} from './your-imai-feed-types';

/**
 * The newest IMAI message any tab has notified about, per chat. A Your IMAI
 * chat belongs to one member in one workspace, so members sharing a browser
 * never claim or dismiss for each other.
 */
const CLAIM_KEY_PREFIX = 'imai:your-imai:notified-through:';
/** Set once the member dismisses the notification prompt, per chat. */
const PROMPT_DISMISSED_KEY_PREFIX = 'imai:your-imai:notify-prompt-dismissed:';

type TaskStatus = YourImaiThreadCard['status'];

/** Where the chat stood when IMAI last finished a turn. */
export type YourImaiTurnMark = {
  latestImaiAt: number | null;
  latestUserTextId: string | null;
  /** Each task Your IMAI started, by org thread id. */
  tasks: ReadonlyMap<string, TaskStatus>;
};

/** What a finished turn tells the member about, if anything. */
export type YourImaiTurnNotice = 'needs_you' | 'failed' | 'done';

/** The tasks as a turn mark keeps them. */
export function yourImaiTaskStatuses(
  tasks: ReadonlyArray<Pick<YourImaiThreadCard, 'orgThreadId' | 'status'>>
): Map<string, TaskStatus> {
  return new Map(tasks.map((task) => [task.orgThreadId, task.status]));
}

/**
 * One notification per turn, and only for something the member must act on
 * or that is finished: IMAI or a task needs them, a task failed, a task
 * delivered, or IMAI answered their own text. A handoff ("on it", a task now
 * working) and status-only relays stay quiet.
 *
 * A turn is finished once IMAI stops writing (or waits for an answer) with
 * something new said since the last finished turn; only then does the mark
 * move, so a task that finishes between turns is told with IMAI's relay.
 */
export function yourImaiTurnOutcome(
  previous: YourImaiTurnMark,
  current: YourImaiTurnMark & {
    status: 'idle' | 'working' | 'failed';
    awaitingAnswer: boolean;
  }
): { finished: boolean; notice: YourImaiTurnNotice | null } {
  const finished =
    (current.status !== 'working' || current.awaitingAnswer) &&
    current.latestImaiAt !== null &&
    current.latestImaiAt > (previous.latestImaiAt ?? 0);
  if (!finished) return { finished, notice: null };
  if (current.status === 'failed') return { finished, notice: 'failed' };
  if (current.awaitingAnswer) return { finished, notice: 'needs_you' };
  const moved: TaskStatus[] = [];
  let handedOff = false;
  for (const [id, status] of current.tasks) {
    const before = previous.tasks.get(id);
    if (before === status) continue;
    if (before === undefined && status === 'working') handedOff = true;
    moved.push(status);
  }
  if (moved.includes('needs_input')) return { finished, notice: 'needs_you' };
  if (moved.includes('failed')) return { finished, notice: 'failed' };
  if (moved.includes('done')) return { finished, notice: 'done' };
  const answeredMember =
    current.latestUserTextId !== null &&
    current.latestUserTextId !== previous.latestUserTextId;
  return { finished, notice: answeredMember && !handedOff ? 'done' : null };
}

/**
 * Whether IMAI's own open question still asks the member something now: its
 * card is in the conversation and IMAI has said nothing since (no text,
 * delivery or task after it). IMAI does not wait on its questions, so one
 * left unanswered up the history stays open while IMAI keeps talking; that
 * old card no longer has the member's attention.
 */
export function yourImaiOpenQuestionAsking(
  rows: readonly YourImaiFeedRow[],
  awaitingToolCallId: string | null
): boolean {
  if (!awaitingToolCallId) return false;
  const index = rows.findIndex(
    (row) =>
      row.kind === 'question' && row.question.toolCallId === awaitingToolCallId
  );
  if (index < 0) return false;
  return !rows
    .slice(index + 1)
    .some(
      (row) =>
        (row.kind === 'bubble' && row.author === 'imai') ||
        row.kind === 'outputs' ||
        row.kind === 'thread' ||
        row.kind === 'question' ||
        row.kind === 'sticker'
    );
}

/**
 * "Get notified" shows right after a delivered result, and only while
 * nothing asks the member anything: never while IMAI or a task works, IMAI
 * waits on their answer, or a task needs them (the question card has that
 * attention). A send held for uploads hides it too (the composer's strip
 * wins; see YOUR_IMAI_COMPOSER_GROUP_CLASS).
 */
export function yourImaiNotifyPromptReady(args: {
  working: boolean;
  awaitingAnswer: boolean;
  tasks: ReadonlyArray<Pick<YourImaiThreadCard, 'status' | 'statusAt'>>;
}): boolean {
  if (args.working || args.awaitingAnswer) return false;
  if (args.tasks.some((task) => task.status === 'working')) return false;
  // Right after a delivered result: the newest task event is a delivery. A
  // task that needs the member since then hides it; one left unanswered
  // before the latest delivery no longer does.
  const at = (task: Pick<YourImaiThreadCard, 'statusAt'>) => task.statusAt ?? 0;
  const delivered = Math.max(
    -1,
    ...args.tasks.filter((task) => task.status === 'done').map(at)
  );
  return (
    delivered >= 0 &&
    !args.tasks.some(
      (task) => task.status === 'needs_input' && at(task) >= delivered
    )
  );
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function claimNow(key: string, messageAt: number): boolean {
  const store = storage();
  if (!store) return true;
  const claimed = Number(store.getItem(key) ?? 0);
  if (Number.isFinite(claimed) && claimed >= messageAt) return false;
  store.setItem(key, String(messageAt));
  return true;
}

/**
 * Claims one turn's notification for this tab, so open tabs show it once.
 * Claims only move forward. Tabs that see the same update together take
 * the claim one at a time under a Web Lock (origin-wide), so exactly one
 * wins; without Web Locks the read and write still run back to back.
 */
export async function claimYourImaiNotification(
  chatId: string,
  messageAt: number
): Promise<boolean> {
  const key = `${CLAIM_KEY_PREFIX}${chatId}`;
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) return claimNow(key, messageAt);
  return await locks.request(key, () => claimNow(key, messageAt));
}

/** Notifications are allowed by the browser and not turned off in IMAI. */
export function yourImaiNotificationsPermitted(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted' &&
    !hasBrowserNotificationOptOut()
  );
}

/** The prompt shows while the browser can still ask and it was not dismissed. */
export function yourImaiNotifyPromptAvailable(chatId: string): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'default' &&
    storage()?.getItem(`${PROMPT_DISMISSED_KEY_PREFIX}${chatId}`) !== '1'
  );
}

export function dismissYourImaiNotifyPrompt(chatId: string): void {
  storage()?.setItem(`${PROMPT_DISMISSED_KEY_PREFIX}${chatId}`, '1');
}
